import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const TEAL = "#0D9488";

/**
 * A small pill shown at the top of the Dashboard for anyone who has donated
 * even $1 through /support. Also listens for a global `supporter:granted`
 * event so the badge shows up instantly right after a successful capture,
 * without waiting for the next /auth/me refresh.
 */
export default function SupporterBadge() {
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/donations/me");
      setStatus(data);
    } catch { /* silent — badge is a nicety */ }
  }, []);

  useEffect(() => {
    load();
    const onGranted = () => load();
    window.addEventListener("supporter:granted", onGranted);
    return () => window.removeEventListener("supporter:granted", onGranted);
  }, [load]);

  if (!status?.is_supporter) return null;

  const since = status.since ? new Date(status.since) : null;
  const sinceLabel = since ? since.toLocaleDateString(undefined, { month: "short", year: "numeric" }) : null;
  const total = Number(status.total_usd || 0);

  return (
    <Link
      to="/support"
      className="inline-flex items-center gap-2 border rounded-full px-3 py-1 transition-colors bg-teal-50 hover:bg-teal-100 border-teal-300"
      title={`You've supported the Exchange with $${total.toFixed(2)}${sinceLabel ? ` since ${sinceLabel}` : ""} — click to give again`}
      data-testid="supporter-badge"
    >
      <Heart className="w-3.5 h-3.5 fill-current" style={{ color: TEAL }} />
      <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>
        Supporter
      </span>
      {total > 0 && (
        <span className="text-[10px] font-mono-num tabular-nums text-teal-700/80 border-l border-teal-300 pl-2">
          ${total.toFixed(0)}
        </span>
      )}
      <Sparkles className="w-3 h-3" style={{ color: TEAL }} />
    </Link>
  );
}
